/**
 * Qualification Detail Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useQualificationDetail hook.
 */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Loader2,
  Send,
  Save,
  Calculator,
  FileText,
} from 'lucide-react';
import {
  useQualificationDetail,
  STATUS_COLORS,
  OverviewTab,
  QuestionsTab,
  ApprovalsTab,
  DocumentsTab,
  ActionDialogs,
} from './qualification-detail';

export default function QualificationDetail() {
  const location = useLocation();
  // CR-6666: Get return URL from location state for proper Back navigation
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  // Supplier's reply when answering a request for more information.
  const [responseNote, setResponseNote] = useState('');

  const {
    // Navigation
    navigate,

    // User info
    isCustomer,
    isSupplier,

    // Loading states
    loading,
    saving,
    actionLoading,

    // Data
    qualification,
    fetchQualification,
    template,
    responses,
    activeTab,
    setActiveTab,

    // Dialog state
    dialogs,
    setDialogs,
    dialogForm,
    setDialogForm,

    // Permissions
    canApprove,
    canReject,
    canRequestInfo,
    canSubmit,
    canCalculateScore,

    // Actions
    handleResponseChange,
    handleFileUpload,
    handleFileDownload,
    handleFileRemove,
    uploadingFiles,
    handleSaveResponses,
    handleSubmit,
    handleApprove,
    handleReject,
    handleRequestInfo,
    handleCalculateScore,
  } = useQualificationDetail();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // CR-6666: Back-navigation uses returnTo state if available (from Due Diligence tab),
  // otherwise falls back to the unified Questionnaires page.
  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/questionnaires');
    }
  };

  if (!qualification) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold">Qualification not found</h2>
        <Button variant="link" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{qualification.template_name}</h1>
              <Badge className={STATUS_COLORS[qualification.status] || STATUS_COLORS['invited']}>
                {qualification.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {isCustomer && `Supplier: ${qualification.supplier_name}`}
              {isSupplier && `Customer: ${qualification.customer_name}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canCalculateScore && (
            <Button variant="outline" onClick={handleCalculateScore} disabled={actionLoading}>
              <Calculator className="h-4 w-4 mr-2" />
              Calculate Score
            </Button>
          )}
          {canRequestInfo && (
            <Button variant="outline" onClick={() => setDialogs(prev => ({ ...prev, info: true }))}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Request Info
            </Button>
          )}
          {canReject && (
            <Button variant="outline" onClick={() => setDialogs(prev => ({ ...prev, reject: true }))} className="text-destructive">
              <ThumbsDown className="h-4 w-4 mr-2" />
              Reject
            </Button>
          )}
          {canApprove && (
            <Button onClick={() => setDialogs(prev => ({ ...prev, approve: true }))}>
              <ThumbsUp className="h-4 w-4 mr-2" />
              Approve
            </Button>
          )}
          {canSubmit && (
            <>
              <Button variant="outline" onClick={handleSaveResponses} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Draft
              </Button>
              <Button onClick={() => handleSubmit()} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Submit
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress & Score */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Completion Progress</span>
                <span className="text-sm text-muted-foreground">
                  {Math.round(qualification.completion_percentage || 0)}%
                </span>
              </div>
              <Progress value={qualification.completion_percentage || 0} className="h-2" />
            </div>
            {qualification.score_percentage !== null && qualification.score_percentage !== undefined && (() => {
              // DRF DecimalField serializes as a string — `"75.50"` — so
              // calling .toFixed() directly blows up the component and the
              // error boundary swallows the whole page. Coerce to a number
              // first.
              const scoreNum = Number(qualification.score_percentage);
              if (Number.isNaN(scoreNum)) return null;
              // Hidden from the responding supplier — see QuestionsTab.
              if (isSupplier) return null;
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Score</span>
                    <span className={`text-sm font-semibold ${qualification.passed ? 'text-green-600' : 'text-red-600'}`}>
                      {scoreNum.toFixed(1)}%
                      {qualification.passed && <CheckCircle2 className="inline h-4 w-4 ml-1" />}
                    </span>
                  </div>
                  <Progress
                    value={scoreNum}
                    className={`h-2 ${qualification.passed ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`}
                  />
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="questions">Questions & Responses</TabsTrigger>
          <TabsTrigger value="documents" className="gap-1">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
        </TabsList>


        {/* Approval history sits with the overview rather than behind its own
            tab: it is the answer to "where did this qualification get to and
            who moved it", which is the question the overview exists to answer.
            A tab of its own hid a single card one click away and split the
            record of a decision from the state that decision produced. */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <OverviewTab
            qualification={qualification}
            isCustomer={isCustomer}
            isSupplier={isSupplier}
          />
          <ApprovalsTab
            qualification={qualification}
            replySlot={
              isSupplier && (qualification.status === 'more_info_required' || qualification.status === 'rejected') ? (
                <div className="rounded-lg border bg-background p-3 space-y-3">
                  <p className="text-sm font-medium">Your reply</p>
                  <Textarea
                    value={responseNote}
                    onChange={(e) => setResponseNote(e.target.value)}
                    placeholder="Answer their question, or say what you changed. e.g. Attached our ISO 27001 certificate and expanded section 3."
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <Button onClick={() => handleSubmit(responseNote)} disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Send reply &amp; resubmit
                    </Button>
                  </div>
                </div>
              ) : undefined
            }
          />
        </TabsContent>

        <TabsContent value="questions" className="mt-4">
          <QuestionsTab
            template={template}
            responses={responses}
            qualificationStatus={qualification.status}
            isSupplier={isSupplier}
            passScore={template?.pass_score}
            onResponseChange={handleResponseChange}
            onFileUpload={handleFileUpload}
            onFileDownload={handleFileDownload}
            onFileRemove={handleFileRemove}
            uploadingFiles={uploadingFiles}
          />
          {/* Same actions repeated under the questions. The form is long
              enough that the header pair scrolls away, leaving a supplier who
              has just answered the last question with no visible way to save
              or submit. */}
          {canSubmit && (
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={handleSaveResponses} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Draft
              </Button>
              <Button onClick={() => handleSubmit()} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Submit
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <DocumentsTab
            qualification={qualification}
            isCustomer={isCustomer}
            onRefresh={fetchQualification}
          />
        </TabsContent>

      </Tabs>

      {/* Action Dialogs */}
      <ActionDialogs
        supplierName={qualification.supplier_name || ''}
        dialogs={dialogs}
        dialogForm={dialogForm}
        actionLoading={actionLoading}
        onDialogsChange={setDialogs}
        onDialogFormChange={setDialogForm}
        onApprove={handleApprove}
        onReject={handleReject}
        onRequestInfo={handleRequestInfo}
      />
    </div>
  );
}
